/**
 * useReportJob hook for managing report generation
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { submitReportJob, getReportJobStatus } from '../api/reports';
import { ReportJobRequest, ReportJobStatus } from '../types/api';
import { useToastStore } from '../stores/toast';

export function useReportJobSubmit() {
  const { addToast } = useToastStore();

  return useMutation<ReportJobStatus, Error, ReportJobRequest>({
    mutationFn: submitReportJob,
    onSuccess: () => {
      addToast({
        type: 'success',
        title: 'Report job submitted',
        message: 'Report generation has started',
      });
    },
    onError: (error) => {
      addToast({
        type: 'error',
        title: 'Failed to submit report job',
        message: error.message,
      });
    },
  });
}

export function useReportJobStatus(jobId: string | null, enabled: boolean = true) {
  return useQuery<ReportJobStatus, Error>({
    queryKey: ['report-job', jobId],
    queryFn: () => getReportJobStatus(jobId!),
    enabled: enabled && !!jobId,
    refetchInterval: (query) => {
      // Poll while job is running or checkpointed
      const data = query.state.data;
      if (data && ['pending', 'queued', 'running', 'checkpointed'].includes(data.status)) {
        return 3000; // Poll every 3 seconds
      }
      // If completed but progress doesn't match, poll one more time to get final update
      if (data && data.status === 'completed') {
        const progress = data.progress_data || {};
        const questionsComplete = progress.questions_complete || 0;
        const totalQuestions = progress.total_questions || 0;
        // If progress shows incomplete but status is completed, poll one more time
        if (totalQuestions > 0 && questionsComplete < totalQuestions) {
          return 1000; // Poll once more after 1 second
        }
      }
      return false; // Stop polling when complete/failed
    },
  });
}

